import React from 'react';
import { Container, Typography, Box, Grid, Button, Paper, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, LocalShipping, Verified, SupportAgent } from '@mui/icons-material';
import bannerImg from '../images/bannerImg.png';
import logoWText from '../images/logoWText.png';

const HomePage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Verified fontSize="large" color="primary" />,
      title: "Premium Quality",
      description: "We use high-grade fabrics that are durable, breathable, and comfortable for all-day school wear."
    },
    {
      icon: <LocalShipping fontSize="large" color="primary" />,
      title: "Timely Delivery",
      description: "Efficient processing and shipping to ensure your child has their uniform ready before school starts."
    },
    {
      icon: <SupportAgent fontSize="large" color="primary" />,
      title: "Dedicated Support",
      description: "Our team is here to help you with sizing, orders, and any questions you might have."
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.dark', 
          color: 'white', 
          pt: 12, 
          pb: 12,
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bannerImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
            Thathva Uniforms
          </Typography>
          <Typography variant="h5" paragraph sx={{ mb: 4, opacity: 0.9 }}>
            Providing high-quality, comfortable, and durable school uniforms for the next generation of leaders.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button 
              variant="contained" 
              size="large" 
              color="primary" 
              onClick={() => navigate('/parent/login')}
              sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              Order Now
            </Button>
            <Button 
              variant="outlined" 
              size="large" 
              sx={{ color: 'white', borderColor: 'white', px: 4, py: 1.5, fontWeight: 'bold', '&:hover': { borderColor: 'primary.light', bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => navigate('/about')}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight="bold" sx={{ mb: 8 }}>
          Why Choose Thathva?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  textAlign: 'center', 
                  bgcolor: '#f8f9fa',
                  borderRadius: 4,
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-10px)' }
                }}
              >
                <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Welcome Section */}
      <Box sx={{ bgcolor: 'white', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                Welcome to Thathva Uniform Portal
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                At Thathva Uniforms, we understand that school uniforms are more than just clothing. They are a symbol of identity, equality, and pride.
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                We have partnered with The Thathva to provide a seamless online ordering experience for parents. Our portal allows you to easily select sizes, choose bundles, and have uniforms delivered directly to your doorstep.
              </Typography>
              <Button 
                variant="text" 
                color="primary" 
                endIcon={<ShoppingBag />} 
                onClick={() => navigate('/parent/login')}
                sx={{ fontWeight: 'bold', fontSize: '1rem' }}
              >
                Start Shopping
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img"
                src={logoWText}
                alt="Thathva Logo"
                sx={{ 
                  width: '100%', 
                  maxWidth: 400, 
                  display: 'block', 
                  mx: 'auto',
                  filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Ready to gear up for the new academic year?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Log in to your parent account to view your child's specific requirements.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large"
            onClick={() => navigate('/parent/login')}
            sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 'bold', '&:hover': { bgcolor: '#f0f0f0' } }}
          >
            Login to Dashboard
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
